import React, { useState, useEffect } from "react";
import axios from "axios";
import "./calllogs.css";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { FaSearch } from "react-icons/fa";
import targetApi from "../services/targetApi";

// ✅ CORE FIX: MySQL via XAMPP returns DATETIME as "2025-06-25T14:30:00.000Z" string
// which JS treats as UTC → adds 5:30 hrs → shows wrong time in IST.
// Solution: strip the Z / timezone suffix and treat it as local time directly.

const parseMySQLDate = (dateVal) => {
  if (!dateVal) return new Date();

  // If it's already a JS Date object, return as-is
  if (dateVal instanceof Date) return dateVal;

  const str = String(dateVal);

  // MySQL datetime string like "2025-06-25T09:00:00.000Z" or "2025-06-25 09:00:00"
  // Remove Z or timezone offset so JS treats it as LOCAL time (IST on your machine)
  // Replace T with space, strip trailing Z or +xx:xx
  const cleaned = str
    .replace("T", " ")           // "2025-06-25 09:00:00.000Z"
    .replace(/\.\d+/, "")        // remove milliseconds → "2025-06-25 09:00:00Z"
    .replace(/Z$/, "")           // remove Z → "2025-06-25 09:00:00"
    .replace(/[+-]\d{2}:\d{2}$/, ""); // remove +05:30 if present

  // Now parse as local time — no timezone shift
  return new Date(cleaned);
};

// ✅ Returns "YYYY-MM-DD" in local time (for date filter comparison)
const toDateString = (dateVal) => {
  const d = parseMySQLDate(dateVal);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ✅ Returns readable display like "25 Jun 2025, 02:30 PM"
const toISTDisplay = (dateVal) => {
  const d = parseMySQLDate(dateVal);
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

function CallLogs() {

  const [selectedDate, setSelectedDate] = useState("");
  const [search, setSearch] = useState("");

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);

  // 🔗 Real "Calls" target assigned by admin (My Target page), instead of
  // a hardcoded number. Every call log entry auto-updates this on the backend.
  const [callsTarget, setCallsTarget] = useState(null);

  const fetchCallsTarget = async () => {
    try {
      const res = await targetApi.getMyCallsTarget();
      setCallsTarget(res.data.target || null);
    } catch (err) {
      console.log(err);
    }
  };

  const [showNote, setShowNote] = useState(false);
  const [activeNote, setActiveNote] = useState("");
  const [activeId, setActiveId] = useState(null);

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    source: "",
    call_status: "",
    notes: "",
    packages: ""
  });

  /* ================= FETCH ================= */
  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("http://localhost:5000/api/calllogs", {
        headers: { Authorization: "Bearer " + token }
      });
      setLogs(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= CHART DATA ================= */
  const chartData = Object.values(
    logs.reduce((acc, log) => {
      const date = toDateString(log.call_date);
      if (!acc[date]) acc[date] = { date, calls: 0 };
      acc[date].calls += 1;
      return acc;
    }, {})
  );

  /* ================= MONTHLY DATA ================= */
  const monthlyData = Object.values(
    logs.reduce((acc, log) => {
      const month = parseMySQLDate(log.call_date).toLocaleString("default", { month: "short" });
      if (!acc[month]) acc[month] = { month, calls: 0 };
      acc[month].calls += 1;
      return acc;
    }, {})
  );

  /* ================= STATUS STATS ================= */
  const statusStats = {
    interested: logs.filter(l => l.call_status === "Interested").length,
    notInterested: logs.filter(l => l.call_status === "Not Interested").length,
    followup: logs.filter(l => l.call_status === "Followup").length
  };

  const pieData = [
    { name: "Interested", value: statusStats.interested },
    { name: "Not Interested", value: statusStats.notInterested },
    { name: "Followup", value: statusStats.followup }
  ];

  const COLORS = ["#22c55e", "#ef4444", "#f59e0b"];

  /* ================= FORM ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const onlyNumbers = value.replace(/\D/g, "");
      if (onlyNumbers.length <= 10) {
        setForm({ ...form, [name]: onlyNumbers });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const addCallLog = async (e) => {
    e.preventDefault();

    if (form.phone.length !== 10) {
      alert("Phone number must be 10 digits");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      await axios.post("http://localhost:5000/api/calllogs", form, {
        headers: { Authorization: "Bearer " + token }
      });

      // ✅ Re-fetch so the new call appears with the correct server timestamp
      await fetchLogs();

      // 🔗 The backend just auto-logged this call against the employee's
      // active "Calls" target - refresh it so Target/Remaining update live.
      await fetchCallsTarget();

      setForm({
        customer_name: "",
        phone: "",
        source: "",
        call_status: "",
        notes: "",
        packages: ""
      });

    } catch (err) {
      console.log(err);
    }
  };

  /* ================= UPDATE ================= */
  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `http://localhost:5000/api/calllogs/${id}`,
        { call_status: status },
        { headers: { Authorization: "Bearer " + token } }
      );

      // ✅ Update local state immediately so UI reflects change without refetch
      setLogs(prev =>
        prev.map(l =>
          l.id === id ? { ...l, call_status: status } : l
        )
      );

    } catch (err) {
      console.log(err);
    }
  };

  const updateNote = async () => {
    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `http://localhost:5000/api/calllogs/${activeId}`,
        { notes: activeNote },
        { headers: { Authorization: "Bearer " + token } }
      );

      setShowNote(false);
      fetchLogs();

    } catch (err) {
      console.log(err);
    }
  };

  /* ================= EXPORT ================= */
  const exportToExcel = () => {
    const headers = ["Name", "Phone", "Source", "Status", "Packages", "Notes", "Date"];

    const rows = filteredLogs.map(log => [
      log.customer_name,
      log.phone,
      log.source,
      log.call_status,
      log.packages,
      log.notes,
      toISTDisplay(log.call_date)
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "call_logs.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ================= LOAD ================= */
  useEffect(() => {
    fetchLogs();
    fetchCallsTarget();
  }, []);

  /* ================= FILTER ================= */
  useEffect(() => {
    let filtered = logs;

    if (selectedDate) {
      filtered = filtered.filter(log => {
        // ✅ Both sides use IST date string for accurate comparison
        return toDateString(log.call_date) === selectedDate;
      });
    }

    if (search) {
      filtered = filtered.filter(log =>
        log.customer_name.toLowerCase().includes(search.toLowerCase()) ||
        log.phone.includes(search) ||
        log.source.toLowerCase().includes(search.toLowerCase()) ||
        log.call_status.toLowerCase().includes(search.toLowerCase())
      );
    }

    filtered = filtered.sort((a, b) => parseMySQLDate(b.call_date) - parseMySQLDate(a.call_date));

    setFilteredLogs(filtered);

  }, [logs, selectedDate, search]);

  /* ================= STATS ================= */
  // ✅ Today's date string in "YYYY-MM-DD" using local time
  const todayIST = toDateString(new Date());

  // ✅ Count calls where date part matches today — uses same parseMySQLDate so no timezone shift
  const todayCount = logs.filter(log =>
    toDateString(log.call_date) === todayIST
  ).length;

  // 🔗 Real target assigned by admin (Target Management) for this employee's
  // "Calls" target - no longer hardcoded. Falls back to 0 / "Not set" if the
  // admin hasn't assigned an active Calls target yet.
  const target = callsTarget ? Number(callsTarget.total_target) : 0;
  const completed = callsTarget ? Number(callsTarget.completed_target) : 0;
  // ✅ Remaining is clamped to 0, never goes negative
  const remaining = callsTarget ? Math.max(0, target - completed) : 0;

  /* ================= UI ================= */
  return (
    <>
      <div className="calllogs-page">
        <div className="calllogs-container">

          {/* ANALYTICS */}
          <div className="analytics-box">
            <div className="card"><h4>Today's Calls</h4><p>{todayCount}</p></div>
            <div className="card">
              <h4>Target</h4>
              <p>{callsTarget ? target : "—"}</p>
            </div>
            <div className="card">
              <h4>Completed</h4>
              <p>{callsTarget ? completed : "—"}</p>
            </div>
            {/* ✅ Remaining updates automatically as new call logs come in */}
            <div className="card"><h4>Remaining</h4><p>{callsTarget ? remaining : "—"}</p></div>

            <div className="search-wrapper">
              <div className="search-box-navbar">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search name, phone, source..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* STATUS GRID */}
          <div className="status-grid">
            <div className="status-card green"><h4>Interested</h4><p>{statusStats.interested}</p></div>
            <div className="status-card red"><h4>Not Interested</h4><p>{statusStats.notInterested}</p></div>
            <div className="status-card yellow"><h4>Followups</h4><p>{statusStats.followup}</p></div>
          </div>

          {/* CHARTS */}
          <div className="dashboard-row">

            <div className="dashboard-card">
              <h3>Monthly Overview</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="calls" stroke="#4f46e5" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="dashboard-card">
              <h3>Status Breakdown</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" outerRadius={90}>
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

          </div>

          {/* FILTER */}
          <div className="filter-box">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <button className="export-btn" onClick={exportToExcel}>Export Excel</button>
          </div>

          {/* FORM */}
          <form className="call-form" onSubmit={addCallLog}>
            <input name="customer_name" placeholder="Customer Name" value={form.customer_name} onChange={handleChange} required />
            <input name="phone" placeholder="Phone" value={form.phone} onChange={handleChange} required />
            <select name="source" value={form.source} onChange={handleChange} required>
              <option value="">Source</option>
              <option>Google My Business</option>
              <option>Social Media</option>
              <option>Website</option>
              <option>Other</option>
            </select>
            <select name="call_status" value={form.call_status} onChange={handleChange} required>
              <option value="">Status</option>
              <option>Interested</option>
              <option>Not Interested</option>
              <option>Followup</option>
            </select>
            <select name="packages" value={form.packages} onChange={handleChange} required>
              <option value="">Packages</option>
              <option>Basic</option>
              <option>Classic</option>
              <option>Advance</option>
              <option>Advance Plus</option>
            </select>
            <input name="notes" placeholder="Notes" value={form.notes} onChange={handleChange} />
            <button>Add</button>
          </form>

          {/* TABLE */}
          <table className="call-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Source</th>
                <th>Status</th>
                <th>Packages</th>
                <th>Notes</th>
                <th>Date & Time</th>
              </tr>
            </thead>

            <tbody>
              {filteredLogs.map((log, index) => (
                <tr key={log.id}>
                  <td>{index + 1}</td>
                  <td>{log.customer_name}</td>
                  <td>{log.phone}</td>
                  <td>{log.source}</td>

                  <td>
                    <select value={log.call_status || ""} onChange={(e) => updateStatus(log.id, e.target.value)}>
                      <option value="">Select</option>
                      <option>Interested</option>
                      <option>Not Interested</option>
                      <option>Followup</option>
                    </select>
                  </td>

                  <td>{log.packages}</td>

                  <td>
                    <div className="note-preview" onClick={() => {
                      setShowNote(true);
                      setActiveNote(log.notes);
                      setActiveId(log.id);
                    }}>
                      {log.notes || "View Note"}
                    </div>
                  </td>

                  {/* ✅ Shows correct IST time with date, month, year, hours & minutes */}
                  <td>{toISTDisplay(log.call_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>
      </div>

      {/* MODAL */}
      {showNote && (
        <div className="note-modal">
          <div className="note-box">
            <div className="note-header">
              <span>Notes</span>
              <button onClick={() => setShowNote(false)}>X</button>
            </div>
            <textarea value={activeNote} onChange={(e) => setActiveNote(e.target.value)} />
            <button className="save-btn" onClick={updateNote}>Save</button>
          </div>
        </div>
      )}
    </>
  );
}

export default CallLogs;